import { TransmissionReport } from './transmissionReport';

export enum DeviceType {
  PM = 'PM',
  ICD = 'ICD',
  CRTD = 'CRT-D',
  CRTP = 'CRT-P',
  ILR = 'ILR'
}

export enum DeviceVendor {
  Medtronic = 'medtronic',
  Biotronik = 'biotronik',
  BostonScientific = 'boston_scientific',
  Abbott = 'abbott',
  Sorin = 'sorin'
}

export const DeviceVendorDetailsMap = {
  [DeviceVendor.Medtronic]: { id: 1, name: 'Medtronic', code: 'ABT' },
  [DeviceVendor.Biotronik]: { id: 2, name: 'Biotronik', code: 'BIO' },
  [DeviceVendor.BostonScientific]: {
    id: 3,
    name: 'Boston Scientific',
    code: 'BSC'
  },
  [DeviceVendor.Abbott]: { id: 4, name: 'Abbott', code: 'MDT' },
  [DeviceVendor.Sorin]: { id: 5, name: 'Sorin', code: 'SOR' }
};

export type Appointment = {
  id: string;
  salesforceRemoteId: string;
  appointmentName: string;
  appointmentNotes?: string | null;
  completedDate?: Date | null;
  cptCode?: string[] | null;
  customFrequency?: number | null;
  encounterDate?: Date | null;
  endDate?: Date | null;
  frequency?: string | null;
  frequencyNumber?: number | null;
  includeEriTracking: boolean;
  isMonthly: boolean;
  scheduledDate?: Date | null;
  emrEncounterId?: string | null;
  status?: string | null;
  transmissionEligibleDate?: Date | null;
  type?: string | null;
  matchDevice?: MatchDevice;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string | null;
  updatedBy?: string;
  isReceived: boolean;
  linkedTransmissionReport?: TransmissionReport;
};

export interface MatchDevice {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  vendor: DeviceVendor;
  modelNumber: string;
  serialNumber: string;
  modelName: string;
  connectivityStatus?: 'Connected' | 'Disconnected' | 'Inductive' | null;
  type: string;
  patientId: string;
  scheduleEnabled: boolean;
  scheduleTransmissions: Array<any>;
  lastTransmissionAt: string | null;
  nextTransmissionAt: string | null;
  scheduleRequiresCorrection: boolean;
  isEriTracked: boolean;
  routineStartDate: string | null;
  routineScheduleTerm: any;
  heartFailureStartDate: string | null;
  heartFailureScheduleTerm: any;
  comboBillingStartDate: string | null;
  comboBillingScheduleTerm: any;
  scheduleNotSet: boolean;
  deviceImplantDate: Date;
  appointments?: Appointment[];
  status?: string;
  implantDate?: string;
  deviceTelemetry?: string;
  isMriCompatibility?: string | boolean;
  leads?: Lead[];
}

export type Lead = {
  id?: string;
  deviceId?: string;
  leadChamber?: string;
  leadLocation?: string | null;
  leadModel?: string;
  serialNumber?: string;
  implantDate?: string | null;
  leadStatus?: string;
  leadCapped?: boolean;
  leadPolarity?: string | null;
  createdAt?: string;
  updatedAt?: string;
  leadManufacturer?: {
    leadVendorId?: number | null;
    leadVendorName?: string;
    leadVendorDescription?: string;
    leadVendorCode?: string;
    createdAt?: string;
    updatedAt?: string;
  };
  recall?: DeviceLeadsRecallResponse;
};

export interface Leads {
  manufacturer?: string;
  model?: string;
  serialNumber?: string;
  implantDate?: string;
  status?: string;
  location?: string;
  capped?: string;
  type?: string;
}

export interface UpdateDeviceDto {
  device: Partial<MatchDevice>;
}

export interface DeviceRecallResponse {
  patientId: string;
  deviceId: string;
  recallClass: string;
  recallInitiationDate: string;
  recallStatus: string;
  reasonForRecall: string;
  voluntaryMandated: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeviceLeadsRecallResponse {
  patientId: string;
  deviceId: string;
  deviceLeadId: string;
  reasonForRecall: string;
  recallInitiationDate: string;
  recallStatus: string;
  recallClass: string;
}

export const getVendorDisplayName = (vendorValue: DeviceVendor): string => {
  const vendorMap: Record<DeviceVendor, string> = {
    [DeviceVendor.Abbott]: 'Abbott',
    [DeviceVendor.Biotronik]: 'Biotronik',
    [DeviceVendor.BostonScientific]: 'Boston Scientific',
    [DeviceVendor.Medtronic]: 'Medtronic',
    [DeviceVendor.Sorin]: 'Sorin'
  };
  return vendorMap[vendorValue] ?? vendorValue;
};
